'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const [user, setUser] = useState<{ username: string } | null>(null);
  const [q, setQ] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    fetch('/api/auth/me')
      .then((r) => r.json())
      .then((d) => setUser(d.user || null))
      .catch(() => setUser(null));
  }, [pathname]);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (q.trim()) router.push(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' });
    setUser(null);
    setMenuOpen(false);
    router.push('/');
    router.refresh();
  };

  const navLink = (href: string, label: string) => {
    const active = pathname === href;
    return (
      <Link
        href={href}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
          active ? 'bg-violet-600/20 text-violet-300' : 'text-zinc-300 hover:bg-zinc-800'
        }`}
      >
        {label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-40 border-b border-zinc-800 bg-zinc-950/90 backdrop-blur">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-14 flex items-center gap-2">
        <Link href="/" className="flex items-center gap-2 mr-1 shrink-0">
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center font-black text-white text-sm">
            A
          </span>
          <span className="font-bold text-lg tracking-tight hidden xs:inline sm:inline">
            Ani<span className="text-violet-400">Track</span>
          </span>
        </Link>

        <form onSubmit={onSearch} className="flex-1 max-w-md ml-1">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search anime…"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-full px-4 py-1.5 text-sm placeholder-zinc-500 focus:outline-none focus:border-violet-500"
          />
        </form>

        <nav className="hidden sm:flex items-center gap-1 ml-auto">
          {navLink('/', 'Home')}
          {navLink('/browse', 'Browse')}
          {navLink('/watchlist', 'Watchlist')}
          {user ? (
            <span className="text-sm text-zinc-400 px-2">👤 {user.username}</span>
          ) : (
            <>
              <Link href="/login" className="px-3 py-1.5 rounded-lg text-sm text-zinc-300 hover:bg-zinc-800">
                Log in
              </Link>
              <Link
                href="/signup"
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-violet-600 hover:bg-violet-500 text-white"
              >
                Sign up
              </Link>
            </>
          )}
        </nav>

        {/* mobile menu button */}
        <button
          className="sm:hidden ml-auto p-2 text-zinc-300"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {menuOpen ? (
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            ) : (
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            )}
          </svg>
        </button>
      </div>

      {menuOpen && (
        <div className="sm:hidden border-t border-zinc-800 bg-zinc-950 px-4 py-3 flex flex-col gap-1">
          <Link href="/" onClick={() => setMenuOpen(false)} className="py-2 text-zinc-200">Home</Link>
          <Link href="/browse" onClick={() => setMenuOpen(false)} className="py-2 text-zinc-200">Browse</Link>
          <Link href="/watchlist" onClick={() => setMenuOpen(false)} className="py-2 text-zinc-200">Watchlist</Link>
          {user ? (
            <button onClick={logout} className="py-2 text-left text-zinc-200">
              Log out ({user.username})
            </button>
          ) : (
            <>
              <Link href="/login" onClick={() => setMenuOpen(false)} className="py-2 text-zinc-200">Log in</Link>
              <Link href="/signup" onClick={() => setMenuOpen(false)} className="py-2 text-violet-300">Sign up</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
